import { isNumber } from 'constants/regexExpressions';
import MarketingStatus from 'enums/marketingStatus';
import createPictureImageSources from 'utils/createPictureImageSources';
import { imagesComingSoonIconSvg } from 'constants/svg-assets';
import CommunityType from 'enums/communityType';
import { ratingStarSvgHtml } from '../../constants/svgIcons';

/**
 * Represents a Paid Summary Card component.
 */
export default class PaidCommunitySummaryCard {
  /**
   * Sets the information for the summary card.
   *
   * @param {Object} summaryCard - The summary card object.
   */
  setInformation(summaryCard) {
    this.summaryCard = summaryCard;
    this.id = 'summaryCard' + this.summaryCard.Id;
    this.isPriceReduction = !!this.summaryCard?.PriceReduced;
    this.wasMatchedAsSpotlightResult = this.summaryCard?.WasMatchedAsSpotlightResult;
    this.isSitePlan = this.summaryCard.SitePlanCenterLatitude && this.summaryCard.SitePlanCenterLongitude;
    this.errorImage = imagesComingSoonIconSvg;
    // TODO: NewHomeSource Rebrand
    // Change sources on backend.
    const { sources, src } = createPictureImageSources({
      url: this.summaryCard.Thumbnail || this.errorImage,
      breakpoints: {
        '2x': {
          width: 286,
          height: 208,
        },
        '3x': {
          width: 350,
          height: 312,
        },
      },
      irsParameters: {
        mode: 'crop',
        progressive: true,
      },
    });
    this.sources = sources;
    this.src = src;
  }

  /**
   * Emits a custom event when a link is clicked on the summary card.
   */
  emitLinkClickEvent(event) {
    if (event.target?.closest('[data-summary-card-cta]')) {
      return;
    }

    const linkClickEvent = new CustomEvent('summaryCardLinkClick', {
      detail: {
        summaryCard: this.summaryCard,
      },
    });

    document.dispatchEvent(linkClickEvent);
  }

  /**
   * Sets the label for the PaidSummaryCard.
   * @param {string} label - The label to set.
   */
  setLabel(label) {
    this.label = label;
  }

  /**
   * Handles the error event for the image element in the summary card.
   * If the image fails to load, it sets the error image as the source and removes all source elements from the parent
   * @param {Event} event - The error event object.
   */
  handleOnErrorImage(event) {
    const picture = event.target.parentElement;
    event.target.src = this.errorImage;

    picture?.querySelectorAll('source').forEach((source) => source.remove());
    event.target.onerror = null;
  }

  /**
   * Returns the call to action based on the summary card properties.
   * @returns {string} The call to action text.
   */
  getCallToAction() {
    const { CommunityProjectType } = this.summaryCard;

    if (this.summaryCard.MarketingStatus.Key === MarketingStatus.ComingSoon.toString()) {
      return 'Get more information';
    }

    if (
      CommunityProjectType.Key !== CommunityType.CustomLocation.toString() &&
      CommunityProjectType.Key !== CommunityType.Custom.toString()
    ) {
      return 'Free brochure';
    }

    return '';
  }

  /**
   * Returns the lead generator opener based on the summary card information.
   * @returns {string} The lead generator opener.
   */
  getLeadGeneratorOpener() {
    if (this.summaryCard.MarketingStatus.Key === MarketingStatus.ComingSoon.toString()) {
      return 'more-information-paid';
    }

    return 'get-brochure-paid';
  }

  /**
   * Emits a custom event when the CTA (Call to Action) is clicked.
   */
  emitCtaClickEvent() {
    const callToAction = this.getCallToAction();
    const element = this.getElement();
    const target = element.dataset.clickTarget;

    const callToActionClickEvent = new CustomEvent('summaryCardCtaClick', {
      detail: {
        name: 'leadGenerator',
        type: this.summaryCard.Type,
        id: this.summaryCard.Id,
        url: this.summaryCard.Url,
        marker: this.summaryCard,
        variation: 'paid',
        callToAction,
        target,
      },
    });

    document.dispatchEvent(callToActionClickEvent);
  }

  /**
   * Binds events to the summary card.
   */
  bindEvents() {
    const element = this.getElement();
    element.addEventListener('click', this.emitLinkClickEvent.bind(this));

    const callToActionButton = element.querySelector('[data-summary-card-cta]');
    callToActionButton?.addEventListener('click', this.emitCtaClickEvent.bind(this));

    const image = element.querySelector('img');

    // remove all sources and set the lifestyle error image as the src for the image
    // if the image fails loading.
    image?.addEventListener('error', this.handleOnErrorImage.bind(this));
  }

  /**
   * Gets the HTML element with the specified ID.
   * @returns {HTMLElement} The HTML anchor element.
   */
  getElement() {
    return document.getElementById(this.id);
  }

  /**
   * Get the price of the summary card.
   * @returns {string} The price of the summary card.
   */
  getPrice() {
    const price = this.summaryCard.Price;
    const containsNumbers = isNumber.test(price);

    if (containsNumbers) {
      return `From ${price}`;
    }

    return price;
  }

  /**
   * Get the price reduced label of the summary card.
   * @returns {string} The price reduced label of the summary card.
   */
  getPriceReducedLabel() {
    if (!this.isPriceReduction) {
      return '';
    }

    return `
      <div class='nhs-n1-c-card__badge nhs-n1-c-card__badge--reduced nhs-u-text--ellipsis' data-qa="reduced-price">
        Reduced
      </div>
    `;
  }

  /**
   * Get the specifications of the summary card.
   * @returns {string} The specifications of the summary card.
   */
  getSpecifications() {
    const specs = [];

    if (this.summaryCard?.FormattedBedrooms) {
      specs.push(`<div><b>${this.summaryCard.FormattedBedrooms}</b> Bd</div>`);
    }

    if (this.summaryCard?.FormattedBathrooms) {
      specs.push(`<div><b>${this.summaryCard.FormattedBathrooms}</b> Ba</div>`);
    }

    if (this.summaryCard?.FormattedGarages) {
      specs.push(`<div><b>${this.summaryCard.FormattedGarages}</b> Gr</div>`);
    }

    if (this.summaryCard?.FormattedSquareFeet) {
      specs.push(`<div><b>${this.summaryCard.FormattedSquareFeet}</b> SqFt</div>`);
    }

    if (specs.length === 0) {
      return '';
    }

    const html = specs.join(' <span>|</span> ');

    return `
            <div class='nhs-c-card--summary__specifications nhs-u-text--ellipsis' data-qa="map_card_specifications">
                ${html}
            </div>
        `;
  }

  /**
   * Returns the label for the community type.
   *
   * - If the summary card represents an adult (55+) community, returns "55+ Community".
   * - Otherwise, returns "Community".
   *
   * @returns {string} The community type label.
   */
  getCommunityTypeLabel() {
    if (this.summaryCard?.IsAdult) {
      return '55+ community in';
    }

    return 'Community in';
  }

  /**
   * Get the title of the summary card.
   * @returns {string} The title of the summary card.
   */
  getPriceTitle() {
    if (this.isPriceReduction) {
      return `
        <h4 class="nhs-c-card--summary__price"  data-qa="map_card_price">          
          <span class="nhs-u-text--ellipsis">${this.getPrice()}</span>          
          ${this.getPriceReducedLabel()}         
        </h4>
      `;
    }

    return `<h4 class="nhs-c-card--summary__price" data-qa="map_card_price">
      <span>${this.getPrice()}</span>
    </h4>`;
  }

  /**
   * Retrieves the image element for the summary card.
   *
   * @returns {string} The HTML string representing the image element.
   */
  getImage() {
    return `
            <picture class="nhs-c-card--summary__image" data-marketId="${this.summaryCard.Id}">
                ${this.sources
                  .map((source) => `<source srcset="${source.srcset}" type="${source.format}"></source>`)
                  .join('')}
                <img 
                    src="${this.src}"
                    data-qa="map-image"
                    data-summary-img
                    class="nhs-c-card--summary__image"
                    data-src="${this.errorImage}"
                >
            </picture>
        `;
  }

  /**
   * Returns the builder information for the summary card.
   * If the builder rating is available, it includes the rating and name.
   * Otherwise, it returns the brand name.
   *
   * @returns {string} The HTML markup for the builder information.
   */
  getBuilder() {
    if (+this.summaryCard.BuilderRating > 0) {
      return `
        <div      
          class="nhs-c-card--summary__builder"     
          aria-label="${this.summaryCard.BuilderRating} out of 5 stars">
            <span data-qa='card-builder-name' class='nhs-u-text--ellipsis'>
              By ${this.summaryCard.BrandName}
            </span>
            <div class="nhs-n1-c-card__rating" data-qa="card-builder-rating">
              <span data-qa='card-builder-rating-icon'>
                ${ratingStarSvgHtml}
              </span>
              <span>
                ${this.summaryCard.BuilderRating}
              </span>
            </div>
        </div>`;
    }

    return `
        <p class="nhs-c-card--summary__builder nhs-u-text--ellipsis" data-qa="map_card_builder">
          <span class="nhs-u-text--ellipsis">By ${this.summaryCard.BrandName}</span>
        </p>
      `;
  }

  /**
   * Returns the HTML string for the interactive site plan button if site plan coordinates are available.
   *
   * - Checks if both SitePlanCenterLatitude and SitePlanCenterLongitude are present.
   * - If available, returns a button element with data attributes for latitude, longitude, and community ID.
   * - If not available, returns an empty string.
   *
   * @returns {string} The HTML string for the interactive site plan button, or an empty string if not applicable.
   */
  getInteractiveSitePlanButton() {
    if (!this.isSitePlan) {
      return '';
    }

    return `<button
      class="nhs-c-button--text-only nhs-c-button--primary-ui nhs-c-button--small-32"
      data-interactive-plan-button
      data-latitude="${this.summaryCard.SitePlanCenterLatitude}"
      data-longitude="${this.summaryCard.SitePlanCenterLongitude}"
      data-community-id="${this.summaryCard.Id}">
      Interactive map
    </button>`;
  }

  /**
   * Returns the HTML string for the call to action required for the mini cards.
   *
   * - Checks if the CTA string is available.
   *
   * @returns {string} The HTML string for the call to action button, or an empty string if not applicable.
   */
  getCallToActionButton() {
    if (this.isSitePlan) {
      return '';
    }

    const callToAction = this.getCallToAction();

    if (!callToAction) {
      return '';
    }

    const opener = this.getLeadGeneratorOpener();

    return `<button
      class="nhs-c-button--text-only nhs-c-button--primary-lead nhs-c-button--small-32"
      data-summary-card-cta 
      data-qa="map-cta" 
      data-lead-generator 
      data-lead-generator-opener="${opener}"
      data-button-action="${callToAction}" 
      data-from-page="CommunityResults.MapCard.Community"
      data-community-id="${this.summaryCard.Id}">
      ${callToAction}
    </button>`;
  }

  /**
   * Returns the HTML representation of the summary card.
   * @returns {string} The HTML string representing the summary card.
   */
  getHtml() {
    const classNames = ['nhs-c-card--summary', 'nhs-mq-card--summary-medium', 'nhs-c-carousel__element'];
    if (this.wasMatchedAsSpotlightResult) {
      classNames.push('nhs-c-card--summary-spotlight');
    }

    return `
            <div id="summaryCard${this.summaryCard.Id}" data-qa="map-card"
                data-summary-content
                data-latitude="${this.summaryCard.Latitude}"
                data-longitude="${this.summaryCard.Longitude}"
                data-community-type="${this.summaryCard.CommunityProjectType.Key}"
                ${this.isPriceReduction ? `data-has-price` : ''}
                ${this.wasMatchedAsSpotlightResult ? `data-was-matched-as-spotlight-result = true` : ''}
                class="${classNames.join(' ')}"
                data-card
                data-id="${this.summaryCard.Id}"
                data-community-id="${this.summaryCard.Id}"
                data-is-coming-soon="${this.summaryCard.MarketingStatus.Key === MarketingStatus.ComingSoon.toString()}"
                data-market-id="${this.summaryCard.MarketId}"
                data-brand-name="${this.summaryCard.BrandName}"
                data-city="${this.summaryCard.City}"
                data-type="${this.summaryCard.Type}"
                data-zip="${this.summaryCard.Zip}"
                data-url="${this.summaryCard.Url}"
                data-has-builder-reviews="${+this.summaryCard.BuilderRating > 0}"
                data-price-low="${this.summaryCard.PriceLow}"
                data-price-high="${this.summaryCard.PriceHigh}"
                data-community-name="${this.summaryCard.Name}"
                data-marketing-status="${this.summaryCard.MarketingStatus.Name}"
                data-price-reduction-label="${this.summaryCard.PriceReductionLabel}"
                data-is-basic="false"
                data-click-target="SRP Map Card"
                data-zip-code="${this.summaryCard.Zip}"
                data-builder-id="${this.summaryCard.BuilderId}"
                data-market-name="${this.summaryCard.MarketName}"
                data-state-abbreviation="${this.summaryCard.StateAbbreviation}"
                data-state-name="${this.summaryCard.StateName}"
            >      
              <a 
                href="${this.summaryCard.Url}" 
                target="_blank"
                class="nhs-c-card--summary__wrapper">
                  ${this.getImage()}
                  <div 
                    data-summary-card-link
                    class="nhs-c-card--summary__body">
                      <h3 class="nhs-c-card--summary__title nhs-u-text--ellipsis" data-qa="map_card_name" >
                        ${this.summaryCard.Name}
                      </h3>
                      ${this.getPriceTitle()}   
                      ${this.getSpecifications()}                 
                      <p class="nhs-c-card--summary__location nhs-u-text--ellipsis" data-qa="map_card_address">
                          ${this.getCommunityTypeLabel()}
                          ${this.summaryCard.City}, 
                          ${this.summaryCard.StateAbbreviation} 
                          ${this.summaryCard.Zip}
                      </p>
                      ${this.getBuilder()}
                  </div>
              </a>
              ${this.getInteractiveSitePlanButton()}
              ${this.getCallToActionButton()}
            </div>
        `;
  }
}
